import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isEmpty } from 'lodash';

import {
  dispatch,
  getRefreshToken,
  getToken,
  getWorkspace,
  getWorkspaces,
  updateUserPermissions,
  updateWorkspace,
  useAppSelector,
  useLazyGetUserPermissionsQuery,
  useLogoutMutation,
  useRegenerateTokenMutation,
  Workspace,
  logOut,
  APP_TYPE,
  updateAppType,
} from '@zonofi/zono-money-store';

import { DrawerItems, useInstaller } from '@zonofi/seller-hub';
import { SideDrawerBase } from '@zonofi/mobile-web-common';

import './side-drawer.scss';
import { WorkspaceSwitcher } from './workspace-switcher';
import { DrawerView } from './drawer-view';

// ─── Public API ───────────────────────────────────────────────────────────────

export interface SideDrawerProps {
  open?: boolean;
  onClose?: () => void;
  /**
   * Initial drawer items.  Each app passes its own DRAWER_ITEMS constant.
   * The component will apply drawerConfig visibility filtering on top.
   */
  drawerItems?: DrawerItems[];
  /**
   * Visibility config record — keys are drawer item ids, values are booleans
   * or { value: boolean } objects.  The component re-applies visibility
   * whenever this reference changes.
   *
   * field-ops: pass the raw Redux getDrawerConfig selector value.
   * seller-hub: fetch from the config API and pass the resolved config object.
   *
   * If omitted, no visibility filtering is applied.
   */
  drawerConfig?: Record<string, unknown>;
  /**
   * Called when the user presses a Clock-In/Out item.
   * Only relevant for field-ops.
   */
  onClockToggle?: () => void;
  /**
   * If provided, the hook for modifying items (e.g. clock-in label/icon sync)
   * is delegated to the caller via this callback which receives the current
   * items and returns updated items.
   */
  transformItems?: (items: DrawerItems[]) => DrawerItems[];
  /**
   * APP_TYPE value used when dispatching updateAppType on logout.
   */
  appType?: APP_TYPE;
  /**
   * Extra logout logic run before the standard token-clear (e.g. field-ops
   * checks the sync queue before logging out).
   */
  onBeforeLogout?: () => Promise<boolean>;
}

// ─── Background colours per variant ──────────────────────────────────────────

const BG_COLOR: Partial<Record<APP_TYPE, string>> = {
  [APP_TYPE.FIELDS_OPS]: '#000000ff',
  [APP_TYPE.SELLER_HUB]: '#1A2B44',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const resolveConfigValue = (raw: unknown): unknown => {
  if (typeof raw === 'object' && raw !== null && 'value' in raw) {
    return (raw as { value?: unknown }).value;
  }
  return raw;
};

const isVisible = (raw: unknown): boolean => {
  const value = resolveConfigValue(raw);
  return !(value === false || value === 'false');
};

// ─── Component ────────────────────────────────────────────────────────────────

export const SideDrawer: React.FC<SideDrawerProps> = ({
  open = false,
  onClose = () => {},
  drawerItems: initialItems = [],
  drawerConfig,
  onClockToggle,
  transformItems,
  appType = APP_TYPE.SELLER_HUB,
  onBeforeLogout,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const currentWorkspace = useAppSelector(getWorkspace);
  const workspacesList = useAppSelector(getWorkspaces);
  const token = useAppSelector(getToken) || '';
  const refreshToken = useAppSelector(getRefreshToken) || '';

  const [getUserPermissionsReq] = useLazyGetUserPermissionsQuery();
  const [regenerateToken] = useRegenerateTokenMutation();
  const [logoutApi] = useLogoutMutation();
  const { canInstall, handleInstall } = useInstaller();

  const [items, setItems] = useState<DrawerItems[]>(initialItems);
  const [switchWorkspace, setSwitchWorkspace] = useState(false);
  const [loadingWorkspaceId, setLoadingWorkspaceId] = useState('');

  // ── Workspaces ─────────────────────────────────────────────────────────────

  const filteredWorkspaces = useMemo(
    () => workspacesList?.filter((w) => w.id !== currentWorkspace?.id),
    [workspacesList, currentWorkspace?.id]
  );

  // ── Drawer config visibility ──────────────────────────────────────────────

  useEffect(() => {
    if (!drawerConfig) return;

    setItems((prev) =>
      prev.map((item) => {
        const updatedItem = {
          ...item,
          show: item.id ? isVisible(drawerConfig[item.id]) : true,
        };
        if (!updatedItem.children?.length) return updatedItem;
        return {
          ...updatedItem,
          children: updatedItem.children.map((child) => ({
            ...child,
            show: child.id ? isVisible(drawerConfig[child.id]) : true,
          })),
        };
      })
    );
  }, [drawerConfig]);

  // ── Expand active parent when drawer opens ─────────────────────────────────

  useEffect(() => {
    if (!open) return;
    const currentFullPath = location.pathname + location.search;

    setItems((prev) =>
      prev.map((item) => {
        if (!item.children) return item;
        const activeChild = item.children.some(
          ({ route }) => route === currentFullPath
        );
        return { ...item, isExpanded: activeChild };
      })
    );
  }, [location.pathname, location.search, open]);

  // ── Caller-supplied item transform (e.g. clock-in sync) ────────────────────

  useEffect(() => {
    if (!transformItems) return;
    setItems((prev) => transformItems(prev));
  }, [transformItems]);

  // ── Workspace switching ────────────────────────────────────────────────────

  const onWorkspaceSelect = async (workspace: Workspace) => {
    setLoadingWorkspaceId(workspace.id);
    try {
      await regenerateToken({
        token,
        refreshToken,
        workspaceId: workspace.id,
      }).unwrap();
      dispatch(updateWorkspace(workspace));
      const response = await getUserPermissionsReq().unwrap();
      if (!isEmpty(response)) dispatch(updateUserPermissions(response));
    } finally {
      setLoadingWorkspaceId('');
      setSwitchWorkspace(false);
      onClose?.();
    }
  };

  // ── Logout ─────────────────────────────────────────────────────────────────

  const handleLogout = async () => {
    if (onBeforeLogout) {
      const shouldProceed = await onBeforeLogout();
      if (!shouldProceed) return;
    }

    try {
      await logoutApi().unwrap();
    } catch (e) {
      console.log('handleLogout', e);
    } finally {
      setTimeout(() => {
        dispatch(logOut());
        dispatch(updateAppType(appType));
      }, 100);
    }
  };

  // ── Drawer item press ──────────────────────────────────────────────────────

  const handleDrawerItemPress = useCallback(
    (route: string, label: string, actionType?: string) => {
      if (actionType === 'CLOCK_TOGGLE') {
        onClockToggle?.();
        onClose?.();
        return;
      }
      if (route) {
        navigate(route);
        onClose?.();
      } else {
        // toggle expand for items with children
        setItems((prev) =>
          prev.map((x) =>
            x.label === label ? { ...x, isExpanded: !x.isExpanded } : x
          )
        );
      }
    },
    [navigate, onClose, onClockToggle]
  );

  // ── Visible items ──────────────────────────────────────────────────────────

  const visibleItems = useMemo(
    () =>
      items
        .filter((item) => item.show !== false)
        .map((item) => ({
          ...item,
          children: item.children?.filter((child) => child.show !== false),
        })),
    [items]
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <SideDrawerBase.Root
      open={open}
      onClose={onClose}
      backgroundColor={BG_COLOR[appType] ?? '#1A2B44'}
    >
      {switchWorkspace ? (
        <WorkspaceSwitcher
          workspace={currentWorkspace as Workspace}
          workspacesList={filteredWorkspaces}
          workspaceId={loadingWorkspaceId}
          onBackPress={() => setSwitchWorkspace(false)}
          onWorkspacePress={onWorkspaceSelect}
        />
      ) : (
        <DrawerView
          drawerItems={visibleItems}
          workspace={currentWorkspace as Workspace}
          workspacesList={filteredWorkspaces}
          onDrawerItemPress={handleDrawerItemPress}
          onCrossPress={onClose}
          canInstall={canInstall}
          handleInstall={handleInstall}
          onSwitchWorkspace={() => setSwitchWorkspace(true)}
          onLogOutPress={handleLogout}
        />
      )}
    </SideDrawerBase.Root>
  );
};
